import { forwardRef, useState } from "react"
import { Camera, Info } from "lucide-react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export const AttributionIcon = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => (
  <button
    type="button"
    ref={ref}
    aria-label="Photo attribution"
    {...props}
    className="flex items-center justify-center bg-transparent border-0 p-0 w-6 h-6"
  >
    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-black/25 backdrop-blur-sm hover:bg-black/35 transition-colors">
      <Info className="w-3.5 h-3.5 text-white" />
    </span>
  </button>
))
AttributionIcon.displayName = `AttributionIcon`

interface AttributionProps {
  attribution?: string | null
  attributionUrl?: string | null
  onChangePhoto?: () => void
  className?: string
  triggerProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
}

export function Attribution({
  attribution,
  attributionUrl,
  onChangePhoto,
  className = `absolute bottom-3 right-3`,
  triggerProps,
}: AttributionProps) {
  if (!attribution && !onChangePhoto) return null

  return (
    <div className={className} onClick={(e) => { e.preventDefault(); e.stopPropagation() }}>
      <AttributionPopover
        attribution={attribution}
        attributionUrl={attributionUrl}
        onChangePhoto={onChangePhoto}
        triggerProps={triggerProps}
      />
    </div>
  )
}

interface AttributionPopoverProps {
  attribution?: string | null
  attributionUrl?: string | null
  onChangePhoto?: () => void
  triggerProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
}

export function AttributionPopover({
  attribution,
  attributionUrl,
  onChangePhoto,
  triggerProps,
}: AttributionPopoverProps) {
  const [open, setOpen] = useState(false)

  const handleChangePhoto = () => {
    setOpen(false)
    onChangePhoto?.()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <AttributionIcon {...triggerProps} />
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="w-auto p-2">
        <div className="space-y-2">
          {attribution && (
            <p className="text-sm text-muted-foreground">
              {attributionUrl ? (
                <>
                  Photo by{` `}
                  <a
                    href={attributionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 underline transition-colors cursor-pointer"
                    title="View photographer profile"
                  >
                    {attribution}
                  </a>
                </>
              ) : (
                attribution
              )}
            </p>
          )}
          {onChangePhoto && (
            <button
              type="button"
              onClick={handleChangePhoto}
              className="flex w-full items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Camera className="w-4 h-4" />
              Change photo
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
